// src/components/Branch/details/BranchDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getBranchThunk,
  updateBranchThunk,
  setMainBranchThunk,
  deactivateBranchThunk,
  updateAdminPrimaryBranchThunk,
} from "../../../store/thunks/branchThunks";
import {
  LocationMarkerIcon,
  PhoneIcon,
  StarIcon,
  CogIcon,
  ChartBarIcon,
  PencilIcon,
  CurrencyRupeeIcon,
  ShieldCheckIcon,
  OfficeBuildingIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ExternalLinkIcon,
  MapIcon,
  UserIcon,
  CalendarIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import StoreList from "../stores/StoreList";
import RateModeConfig from "../config/RateModeConfig";
import EditBranchForm from "../forms/EditBranchForm";
import BranchSelectionModal from "../modals/BranchSelectionModal";
import BranchAnalytics from "./BranchAnalytics";
import WorkingHoursConfig from "../config/WorkingHoursConfig";
import {
  STATE_CODES,
  CITY_TIERS,
  BRANCH_TYPES,
  RATE_POLICIES,
  RATE_MODES,
} from "../../../constants/branch.constants";
import HybridPolicyDisplay from "../config/HybridPolicyDisplay";
import BranchOwnershipManagement from "../config/BranchOwnershipManagement";
import ExchangeChargesManagement from "../config/ExchangeChargesManagement";
import AppointmentConfigManagement from "../config/AppointmentConfigManagement";
import BranchInstallmentManagement from "../config/BranchInstallmentManagement";
import BranchDisplayManagement from "../config/BranchDisplayManagement";

const BranchDetails = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedBranch, stores, loadingDetails, currentRates } = useSelector(
    (state) => state.branch,
  );
  const { user: currentAdmin } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  useEffect(() => {
    dispatch(getBranchThunk(branchId));
  }, [dispatch, branchId]);

  const handleSetMainBranch = async () => {
    if (
      window.confirm(
        "Set this as the main branch? This will remove main status from other branches.",
      )
    ) {
      await dispatch(setMainBranchThunk(branchId));
      dispatch(getBranchThunk(branchId));
    }
  };

  const handleDeactivate = async () => {
    const reason = prompt("Please provide a reason for deactivation:");
    if (reason) {
      await dispatch(deactivateBranchThunk({ branchId, reason }));
      navigate("/branches");
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    dispatch(getBranchThunk(branchId));
  };

  const handleCopySuccess = () => {
    setShowCopyModal(false);
    dispatch(getBranchThunk(branchId));
  };

  const handleSetAsMyPrimary = async () => {
    if (!currentAdmin?._id || !selectedBranch?.code) {
      alert("Unable to update primary branch");
      return;
    }

    if (currentAdmin.branchAccess?.primaryBranch === selectedBranch.code) {
      alert("This is already your primary branch");
      return;
    }

    if (
      window.confirm(
        `Set ${selectedBranch.name} (${selectedBranch.code}) as your primary branch?`,
      )
    ) {
      try {
        await dispatch(
          updateAdminPrimaryBranchThunk({
            adminId: currentAdmin._id,
            branchCode: selectedBranch.code,
          }),
        ).unwrap();
      } catch (error) {
        alert(`Failed to update primary branch: ${error}`);
      }
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "—";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN");
  };

  if (loadingDetails || !selectedBranch) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <EditBranchForm
        branch={selectedBranch}
        onClose={() => setIsEditing(false)}
        onSuccess={handleEditSuccess}
      />
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: ChartBarIcon },
    { id: "details", label: "Details", icon: CogIcon },
    { id: "display", label: "Display", icon: EyeIcon },
    { id: "financial", label: "Financial", icon: CurrencyRupeeIcon },
    { id: "compliance", label: "Compliance", icon: ShieldCheckIcon },
    { id: "exchange", label: "Gold Exchange Charges", icon: CurrencyRupeeIcon },
    { id: "appointments", label: "Appointments", icon: CalendarIcon },
    { id: "installments", label: "Installments", icon: CurrencyRupeeIcon },
    { id: "ownership", label: "Ownership & Access", icon: UserIcon },
    { id: "stores", label: "Stores", icon: OfficeBuildingIcon },
    { id: "rates", label: "Rate Config", icon: ChartBarIcon },
    { id: "hours", label: "Working Hours", icon: ClockIcon },
    { id: "analytics", label: "Analytics", icon: ChartBarIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{selectedBranch.name}</h1>
              {selectedBranch.isMainBranch && (
                <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 rounded-full">
                  <StarIcon className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Main Branch
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 text-gray-600">
              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                {selectedBranch.code}
              </span>
              <span className="text-sm">
                {BRANCH_TYPES[selectedBranch.branchType] ||
                  selectedBranch.branchType}
              </span>
              <span
                className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                  selectedBranch.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {selectedBranch.isActive ? "Active" : "Inactive"}
              </span>
            </div>

            {selectedBranch.parentBranchId && (
              <div className="mt-2 text-sm text-gray-500">
                Parent Branch:{" "}
                <span className="font-medium">
                  {typeof selectedBranch.parentBranchId === "object"
                    ? `${selectedBranch.parentBranchId.name} (${selectedBranch.parentBranchId.code})`
                    : selectedBranch.parentBranchId}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowCopyModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Copy Config
            </button>

            <button
              onClick={handleSetAsMyPrimary}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              title="Set this branch as your primary branch"
            >
              <UserIcon className="h-4 w-4" />
              Set as My Primary
            </button>

            {currentAdmin?.role === "root" && !selectedBranch.isMainBranch && (
              <button
                onClick={handleSetMainBranch}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
              >
                <StarIcon className="h-4 w-4" />
                Set as Main
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>

            {selectedBranch.isActive &&
              !selectedBranch.isMainBranch &&
              currentAdmin?.role === "root" && (
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Deactivate
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quick Info Card */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Quick Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch Type:</span>
                    <span className="font-medium">
                      {BRANCH_TYPES[selectedBranch.branchType] ||
                        selectedBranch.branchType ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">City Tier:</span>
                    <span className="font-medium">
                      {CITY_TIERS[selectedBranch.cityTier] ||
                        selectedBranch.cityTier ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium">
                      {selectedBranch.address?.city || "—"},{" "}
                      {STATE_CODES[selectedBranch.address?.state] ||
                        selectedBranch.address?.state ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Store Count:</span>
                    <span className="font-medium">
                      {selectedBranch.storeCount || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Establishment:</span>
                    <span className="font-medium">
                      {selectedBranch.compliance?.establishmentYear || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rate Configuration Card */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Rate Configuration</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Policy:</span>
                    <span className="font-medium">
                      {RATE_POLICIES[selectedBranch.rateConfig?.ratePolicy] ||
                        selectedBranch.rateConfig?.ratePolicy ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mode:</span>
                    <span className="font-medium">
                      {RATE_MODES[selectedBranch.rateConfig?.rateMode] ||
                        selectedBranch.rateConfig?.rateMode ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auto Sync:</span>
                    <span className="font-medium">
                      {selectedBranch.rateConfig?.autoSync?.enabled
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </div>
                  {selectedBranch.rateConfig?.autoSync?.enabled && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Frequency:</span>
                      <span className="font-medium">
                        {selectedBranch.rateConfig?.autoSync?.frequency || "—"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Primary Contact</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 text-gray-500" />
                    <span>{selectedBranch.contact?.phoneNumber || "—"}</span>
                  </div>
                  {selectedBranch.contact?.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">@</span>
                      <span>{selectedBranch.contact.email}</span>
                    </div>
                  )}
                  {selectedBranch.contact?.managerName && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Manager:</span>
                      <span>{selectedBranch.contact.managerName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Settings Card */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Key Settings</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">High Value Threshold:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        selectedBranch.settings?.highValueThreshold,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Discount:</span>
                    <span className="font-medium">
                      {selectedBranch.settings?.maxDiscountPercent ?? "—"}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Cross-Branch Redemption:
                    </span>
                    <span className="font-medium">
                      {selectedBranch.settings?.allowCrossBranchRedemption
                        ? "Yes"
                        : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Exchange Charges Card */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Exchange Charges</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Melting Loss:</span>
                    <span className="font-medium">
                      {selectedBranch.settings?.exchangeCharges
                        ?.meltingLossPercent ?? 1.5}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Wastage:</span>
                    <span className="font-medium">
                      {selectedBranch.settings?.exchangeCharges
                        ?.wastagePercent ?? 2}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Handling Charges:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        selectedBranch.settings?.exchangeCharges
                          ?.handlingCharges ?? 100,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Min Exchange Value:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        selectedBranch.settings?.exchangeCharges
                          ?.minExchangeValue ?? 1000,
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Display Tab */}
          {activeTab === "display" && (
            <BranchDisplayManagement branch={selectedBranch} />
          )}

          {activeTab === "exchange" && (
            <ExchangeChargesManagement branch={selectedBranch} />
          )}

          {activeTab === "ownership" && (
            <BranchOwnershipManagement branch={selectedBranch} />
          )}

          {activeTab === "appointments" && (
            <AppointmentConfigManagement branch={selectedBranch} />
          )}

          {activeTab === "installments" && (
            <BranchInstallmentManagement branch={selectedBranch} />
          )}

          {/* Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6">
              {/* Address Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <LocationMarkerIcon className="h-5 w-5 mr-2" />
                  Address & Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Full Address</p>
                    {selectedBranch.address ? (
                      <>
                        <p className="font-medium">
                          {selectedBranch.address.line1 || "—"}
                        </p>
                        {selectedBranch.address.line2 && (
                          <p className="font-medium">
                            {selectedBranch.address.line2}
                          </p>
                        )}
                        <p className="font-medium">
                          {selectedBranch.address.city || "—"},{" "}
                          {STATE_CODES[selectedBranch.address.state] ||
                            selectedBranch.address.state ||
                            "—"}{" "}
                          - {selectedBranch.address.pinCode || "—"}
                        </p>
                        {selectedBranch.address.landmark && (
                          <p className="text-sm text-gray-600 mt-2">
                            Landmark: {selectedBranch.address.landmark}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-500">No address available</p>
                    )}
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">
                      Geographic Details
                    </p>
                    {selectedBranch.address?.latitude &&
                    selectedBranch.address?.longitude ? (
                      <>
                        <p className="text-sm">
                          Lat: {selectedBranch.address.latitude}, Long:{" "}
                          {selectedBranch.address.longitude}
                        </p>
                        {selectedBranch.address.googleMapsUrl && (
                          <a
                            href={selectedBranch.address.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm mt-2"
                          >
                            <MapIcon className="h-4 w-4" />
                            View on Google Maps
                            <ExternalLinkIcon className="h-3 w-3" />
                          </a>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No coordinates available
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <PhoneIcon className="h-5 w-5 mr-2" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Primary Phone</p>
                    <p className="font-medium">
                      {selectedBranch.contact?.phoneNumber || "—"}
                    </p>
                    {selectedBranch.contact?.alternatePhone && (
                      <>
                        <p className="text-sm text-gray-600 mb-1 mt-2">
                          Alternate Phone
                        </p>
                        <p className="font-medium">
                          {selectedBranch.contact.alternatePhone}
                        </p>
                      </>
                    )}
                    {selectedBranch.contact?.whatsappNumber && (
                      <>
                        <p className="text-sm text-gray-600 mb-1 mt-2">
                          WhatsApp
                        </p>
                        <p className="font-medium">
                          {selectedBranch.contact.whatsappNumber}
                        </p>
                      </>
                    )}
                  </div>

                  {selectedBranch.contact?.email && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Email</p>
                      <p className="font-medium">
                        {selectedBranch.contact.email}
                      </p>
                    </div>
                  )}

                  {selectedBranch.contact?.managerName && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Manager</p>
                      <p className="font-medium">
                        {selectedBranch.contact.managerName}
                      </p>
                      {selectedBranch.contact?.managerPhone && (
                        <p className="text-sm">
                          {selectedBranch.contact.managerPhone}
                        </p>
                      )}
                      {selectedBranch.contact?.managerEmail && (
                        <p className="text-sm">
                          {selectedBranch.contact.managerEmail}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === "financial" && (
            <div className="space-y-6">
              {selectedBranch.financial?.bankAccount ? (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
                    Bank Account Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBranch.financial.bankAccount.accountName && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">
                          Account Name
                        </p>
                        <p className="font-medium">
                          {selectedBranch.financial.bankAccount.accountName}
                        </p>
                      </div>
                    )}
                    {selectedBranch.financial.bankAccount.bankName && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Bank Name</p>
                        <p className="font-medium">
                          {selectedBranch.financial.bankAccount.bankName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CurrencyRupeeIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No financial information available</p>
                </div>
              )}
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === "compliance" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 mr-2" />
                  Compliance & Legal
                </h3>
                {selectedBranch.compliance?.gstNumber ||
                selectedBranch.compliance?.panNumber ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBranch.compliance?.gstNumber && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">GST Number</p>
                        <p className="font-mono font-medium">
                          {selectedBranch.compliance.gstNumber}
                        </p>
                      </div>
                    )}
                    {selectedBranch.compliance?.panNumber && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">PAN Number</p>
                        <p className="font-mono font-medium">
                          {selectedBranch.compliance.panNumber}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ShieldCheckIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No compliance information available</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "stores" && (
            <StoreList branchId={branchId} stores={stores} />
          )}

          {activeTab === "rates" && <RateModeConfig branch={selectedBranch} />}

          {activeTab === "hours" && (
            <WorkingHoursConfig
              workingHours={selectedBranch.settings?.workingHours}
              onChange={() => {}}
              viewOnly={true}
            />
          )}

          {activeTab === "analytics" && <BranchAnalytics branchId={branchId} />}
        </div>
      </div>

      {/* Copy Configuration Modal */}
      <BranchSelectionModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        targetBranchId={branchId}
        onSuccess={handleCopySuccess}
      />
    </div>
  );
};

export default BranchDetails;
